import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  Archive,
  Package,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { applicationLabel } from '@aeris/catalog'
import type { MissingField, ProductSummary } from '@aeris/contracts'
import {
  Button,
  Checkbox,
  ColumnChooser,
  Combobox,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  ActiveFilters,
  FilterableHeaderCell,
  RowActions,
  RowOpener,
  SearchInput,
  SearchModeToggle,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableActionsCell,
  TableActionsHeaderCell,
  TableRow,
  VisuallyHidden,
  notify,
  type ActiveFilterChip,
  type ColumnFilterSpec,
  type ColumnFilterValue,
} from '@aeris/ui'

import { CategorizeProductsDialog } from '../components/categorize-products-dialog.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { ImportCategoriesDialog } from '../components/import-categories-dialog.tsx'
import {
  LabelPrintDialog,
  type LabelPrintProduct,
} from '../components/label-print-dialog.tsx'
import { MinimumStockDialog } from '../components/minimum-stock-dialog.tsx'
import { ProductDetailDialog } from '../components/product-detail-dialog.tsx'
import { ProductForm } from '../components/product-form.tsx'
import { fetchBrands } from '../lib/api/brands.ts'
import { fetchProductCategories } from '../lib/api/product-categories.ts'
import {
  archiveProduct,
  fetchCompany,
  fetchBranches,
  fetchPriceLists,
  fetchApplicationModels,
  fetchProducts,
  restoreProduct,
  setProductsStockTracking,
  hideProductsFromStorefront,
  shelveProducts,
  showProductsOnStorefront,
  unshelveProducts,
  fetchProductLabelInfo,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { categoryLabel } from '../lib/category-label.ts'
import {
  enumValue,
  quantityRangeLabel,
  rangeLabel,
} from '../lib/column-filters.ts'
import { formatDate } from '../lib/date-format.ts'
import { addToLabelQueue, droppedMessage } from '../lib/label-queue.ts'
import {
  CONDITION_LABEL,
  DEFAULT_PRODUCT_COLUMNS,
  PRODUCT_COLUMNS,
  type ProductSortKey,
} from '../lib/product-columns.tsx'
import { productExportColumns } from '../lib/product-export.ts'
import { shelfMessage } from '../lib/shelf-message.ts'
import { stockDisabledBecause } from '../lib/stock-policy.ts'
import {
  availableColumns,
  shownColumnsOf,
  toggledColumns,
} from '../lib/table-columns.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { usePreference } from '../lib/use-preference.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Product list.
 *
 * The pattern every future list screen copies: server-side search, server-side
 * pagination, and a page size the client cannot raise past the server's cap.
 * Master Specification section 27 sets the baseline at 100.000 products and
 * forbids rendering thousands of rows, so the list never holds more than one
 * page in memory.
 *
 * Writing is gated on `catalog.write`. That only hides the buttons — the API
 * refuses the request independently, which is what actually enforces it.
 */

const PAGE_SIZE = 25

/**
 * A seleção, guardada de dois jeitos conforme o que a pessoa fez (F070).
 *
 * `all: true` é "todos os que casam o filtro, menos estes" — o caso do dono:
 * 5309 produtos, seis exceções. Guardar o complemento é o que faz a seleção
 * atravessar páginas sem carregar nenhuma: a alternativa, acumular os ids
 * página a página, obrigaria a percorrer 213 páginas para que "todos"
 * quisesse dizer todos, e perderia as escolhas de quem volta uma página.
 *
 * `all: false` é "estes e só estes", para quem marcou três caixas sem nunca
 * ter pedido "todos". Sem esse segundo modo, a caixa de linha não faria nada
 * até alguém clicar no cabeçalho, que é uma tabela onde clicar não responde.
 */
type Selection =
  | { readonly all: true; readonly excluded: ReadonlySet<string> }
  | { readonly all: false; readonly included: ReadonlySet<string> }

const NOTHING_SELECTED: Selection = { all: false, included: new Set() }

/** "5.309", e não "5309" (F248): a contagem que a tela anuncia, com milhar. */
const count = (value: number): string => value.toLocaleString('pt-BR')

const withToggled = (
  ids: ReadonlySet<string>,
  id: string
): ReadonlySet<string> => {
  const next = new Set(ids)
  if (!next.delete(id)) next.add(id)
  return next
}

export const ProdutosPage = (): ReactNode => {
  const { allows, session } = useAuth()
  const queryClient = useQueryClient()

  /**
   * A resposta da loja sobre estoque, para o formulário de produto novo (F070).
   *
   * Carregada aqui e não dentro do diálogo: quando alguém clica em "Novo
   * produto" a lista já está na tela há segundos, então a resposta está pronta
   * e a caixa abre certa de primeira. Buscá-la no mount do diálogo faria a
   * caixa aparecer marcada e se desmarcar sozinha um instante depois.
   *
   * Todo papel — até o caixa — tem `organization.read`, então não há aqui um
   * caminho em que a consulta seja recusada para quem pode cadastrar produto.
   */
  const companyId = session?.context?.companyId ?? null

  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null,
  })

  /**
   * A razão, ou nula quando a loja conta normalmente (F102).
   *
   * Enquanto a consulta carrega, trata-se como LIGADO. Desabilitar otimista e
   * reabilitar um instante depois é uma caixa que pisca, e uma tela que muda
   * de ideia debaixo do cursor é pior do que uma que demora a saber.
   */
  const stockOff =
    company.data !== undefined && !company.data.company.salesFlow.countsStock
      ? stockDisabledBecause(
          company.data.company.tradeName ?? company.data.company.legalName
        )
      : null

  const [search, setSearch] = useState('')
  /*
   * "Aguardando estoque" (F133) é um recorte dos ATIVOS, e não uma terceira
   * situação: no servidor vira `status=active&awaitingStock=true`.
   *
   * "Sem estoque" (03/10) também: os ativos com saldo até zero na filial do
   * estoque. A loja pediu *"puxar todas as baterias que estão sem estoque,
   * exportar e conferir na Elite principal"* — o filtro existia, na faixa do
   * cabeçalho da coluna Estoque, onde ninguém acha (e no celular, nem existe).
   */
  const [status, setStatus] = useState<
    'active' | 'archived' | 'awaiting' | 'out' | 'shelved' | 'sitehidden'
  >('active')
  /*
   * F247: o "arquivado até ter estoque" sai das vistas do dia a dia e tem a
   * sua própria — e o que falta no cadastro (foto, categoria, preço) é um
   * recorte que vale em qualquer uma delas.
   */
  const [missing, setMissing] = useState<'' | MissingField>('')
  const shelvedFilter =
    status === 'shelved'
      ? { shelved: 'only' as const }
      : status === 'archived'
        ? {}
        : status === 'sitehidden'
          ? { siteHidden: 'only' as const }
          : { shelved: 'exclude' as const }
  const statusFilter = {
    status: status === 'archived' ? ('archived' as const) : ('active' as const),
    ...(status === 'awaiting' ? { awaitingStock: true } : {}),
    ...(status === 'out' ? { stockMax: '0' } : {}),
    ...shelvedFilter,
    ...(missing === '' ? {} : { missing: [missing] }),
  }
  const [page, setPage] = useState(1)

  const [selection, setSelection] = useState<Selection>(NOTHING_SELECTED)
  const navigate = useNavigate()
  const [labelBatch, setLabelBatch] = useState<{
    pending: boolean
    error: string | null
  }>({ pending: false, error: null })
  const [confirming, setConfirming] = useState<boolean | null>(null)
  // F247: "Arquivar sem estoque" e "Mostrar de novo", cada um com confirmação.
  const [shelfConfirm, setShelfConfirm] = useState<
    'shelve' | 'unshelve' | null
  >(null)
  // F291: "Ocultar do site" e "Mostrar no site".
  const [siteConfirm, setSiteConfirm] = useState<'hide' | 'show' | null>(null)
  const [bulkResult, setBulkResult] = useState<string | null>(null)

  /**
   * A seleção morre quando o CONJUNTO muda, e sobrevive quando só a vista muda.
   *
   * É a distinção inteira desta feature em uma função. Trocar a busca, a
   * situação ou a aplicação redefine quais produtos existem no conjunto, e
   * "todos os 5309" passaria a querer dizer outros produtos que não os que a
   * pessoa tinha na frente quando decidiu. Virar a página, ordenar por preço
   * ou trocar a tabela de preço não mexe em quem está no conjunto — e ali a
   * seleção tem de continuar de pé, senão "marcar todos e tirar alguns" seria
   * impossível de fazer em mais de uma página.
   *
   * Chamado nos mesmos manipuladores que já zeram a página, e não num efeito
   * que observa o filtro: o reset é consequência direta do clique, e um efeito
   * o faria um render depois, com a barra de ação exibindo por um instante uma
   * contagem que já não vale.
   */
  const resetSelection = (): void => {
    setSelection(NOTHING_SELECTED)
    setBulkResult(null)
  }

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<ProductSortKey>('name', 'asc', () => {
    setPage(1)
  })

  /**
   * Which table and which shop the two right-hand columns are talking about.
   *
   * Empty means "the account's default" and "the branch of this session" — the
   * server resolves both and says which it used, so the selectors start on the
   * real answer rather than on a guess. They are on the screen because a price
   * with no table named is not a fact, and a balance with no branch named is
   * an average of nothing.
   */
  /**
   * "Which parts fit this model?" (F031).
   *
   * A filter on this list rather than its own screen, so the search box, the
   * status filter and the sort keep working on top of it — the counter narrows
   * to an A20 and then still types "tela".
   */
  const [applicationModelId, setApplicationModelId] = useState('')
  const [modelSearch, setModelSearch] = useState('')
  const [priceListId, setPriceListId] = useState('')
  const [branchId, setBranchId] = useState('')

  const priceLists = useQuery({
    queryKey: ['price-lists', 'active'],
    queryFn: ({ signal }) => fetchPriceLists(signal),
    enabled: allows('pricing.read'),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
    enabled: allows('inventory.read'),
  })

  const showsPrice = allows('pricing.read')
  const showsStock = allows('inventory.read')

  /*
   * As colunas desta pessoa (F127), guardadas no servidor: valem em qualquer
   * computador em que ela entrar. Quem nunca escolheu vê as de sempre.
   */
  const columnsPreference = usePreference('table.products.columns', {
    visible: [...DEFAULT_PRODUCT_COLUMNS],
  })

  /** Onde a busca procura (F167). A escolha viaja com a pessoa, como as colunas. */
  const searchModePreference = usePreference('search.mode', {
    products: 'anywhere',
    stock: 'anywhere',
  })
  const searchMode = searchModePreference.value.products
  const offeredColumns = availableColumns(PRODUCT_COLUMNS, allows)
  const shownColumns = shownColumnsOf(
    offeredColumns,
    columnsPreference.value.visible
  )
  const showsPriceChangedAt = shownColumns.some(
    (column) => column.id === 'priceChangedAt'
  )
  const [editing, setEditing] = useState<ProductSummary | undefined>(undefined)
  const [formOpen, setFormOpen] = useState(false)

  // 300 ms: long enough that a typed word is one request, short enough that the
  // list feels attached to the keyboard.
  const debouncedSearch = useDebounced(search)

  const canWrite = allows('catalog.write')

  /*
   * A etiqueta carrega o preço DA PRATELEIRA — F124.
   *
   * Quando a pessoa está olhando uma tabela de preço que não é a padrão (a de
   * atacado, a de um convênio), o número na tela não é o que vai no adesivo da
   * peça. Enfileirar aquele valor produziria uma etiqueta de vitrine com preço
   * de atacado, e ninguém notaria até o cliente apontar.
   *
   * Então a ação fica indisponível em vez de imprimir o preço errado. A
   * alternativa seria uma segunda consulta ao preço padrão só para isto, e
   * trocar de tabela é um clique.
   */
  const viewingDefaultPriceList =
    priceListId === '' ||
    priceLists.data?.priceLists.find((list) => list.id === priceListId)
      ?.isDefault === true

  /*
   * O produto cuja etiqueta está sendo impressa (F127). O diálogo escolhe
   * modelo, quantidade e mostra as folhas; a fila fica para o lote.
   */
  const [labelProduct, setLabelProduct] = useState<LabelPrintProduct | null>(
    null
  )

  const openLabel = (product: ProductSummary): void => {
    const variant =
      product.variants.find((entry) => entry.isDefault) ?? product.variants[0]
    if (variant === undefined) return
    setLabelProduct({
      variantId: variant.id,
      productId: product.id,
      name: product.name,
      sku: variant.sku,
      gtin: variant.gtin,
      brand: product.brand,
      unit: variant.unit,
      supplierReference: variant.supplierReference ?? null,
      location: product.location ?? null,
      // Sem `pricing.read` a etiqueta sai sem preço, e é isso mesmo: quem não
      // pode ver o preço não pode imprimi-lo.
      salePrice: product.price?.basePrice ?? null,
      // O `ULTIMA_COMPRA` dos modelos do GDOOR. Só vem para quem edita o
      // produto (`include=cost`); para os outros o campo sai em branco.
      lastPurchaseAt:
        product.cost?.lastPurchaseAt == null
          ? null
          : formatDate(product.cost.lastPurchaseAt),
    })
  }

  const debouncedModelSearch = useDebounced(modelSearch, 150)

  /** The application register, for the "serve em..." filter. */
  const models = useQuery({
    queryKey: ['application-models', 'search', debouncedModelSearch],
    queryFn: async ({ signal }) =>
      await fetchApplicationModels(
        { search: debouncedModelSearch, limit: 20 },
        signal
      ),
  })

  /*
   * O filtro que está na tela, sem a página. A lista pede uma página dele; a
   * exportação (F131) pede todas — e as duas não podem discordar do filtro.
   */
  /** Os filtros de coluna (F152). */
  const [brand, setBrand] = useState('')
  /** A família da peça (F163). Vazio é "todas". */
  const [categoryId, setCategoryId] = useState('')
  const [categorizing, setCategorizing] = useState(false)
  /** F288: o mínimo da categoria inteira. */
  const [settingMinimum, setSettingMinimum] = useState(false)
  const [importingCategories, setImportingCategories] = useState(false)

  /*
   * As marcas do cadastro, para o menu da coluna — F158.
   *
   * O filtro era texto livre, o que exigia que a pessoa ADIVINHASSE a grafia do
   * que procura: digitar "multi" não acha "Multilaser" (o servidor compara por
   * igualdade, sem curinga) e digitar "Multilaser" só acha se for essa a
   * grafia. Com o cadastro atrás, a coluna oferece o que existe.
   *
   * A lista inteira, sem busca: são dezenas de marcas. O mesmo raciocínio de
   * `brand-field.tsx`.
   */
  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: async ({ signal }) =>
      await fetchProductCategories('active', signal),
  })

  const brands = useQuery({
    queryKey: ['brands', 'active'],
    queryFn: async ({ signal }) => await fetchBrands('active', signal),
  })
  const [condition, setCondition] = useState<'' | ProductSummary['condition']>(
    ''
  )
  const [priceRange, setPriceRange] = useState<{
    kind: 'range'
    min: string
    max: string
  } | null>(null)
  const [stockRange, setStockRange] = useState<{
    kind: 'range'
    min: string
    max: string
  } | null>(null)

  const listRequest = {
    ...(debouncedSearch.trim().length > 0
      ? { search: debouncedSearch.trim(), searchMode }
      : {}),
    ...statusFilter,
    sort: sort.sort,
    direction: sort.direction,
    include: [
      ...(showsPrice ? (['price'] as const) : []),
      ...(showsStock ? (['stock'] as const) : []),
      // O custo abre preenchido na ficha (F125) — só para quem edita.
      ...(canWrite ? (['cost'] as const) : []),
      // Só quando a coluna está na tela: é uma consulta a mais.
      ...(showsPriceChangedAt ? (['priceChangedAt'] as const) : []),
    ],
    ...(priceListId === '' ? {} : { priceListId }),
    ...(branchId === '' ? {} : { branchId }),
    ...(applicationModelId === '' ? {} : { applicationModelId }),
    // Os filtros de coluna (F152). São os mesmos de `currentFilter`, que é o
    // que a ação em massa manda — e é essa igualdade que a F070 protege.
    ...(brand.trim() === '' ? {} : { brand: brand.trim() }),
    ...(categoryId === '' ? {} : { categoryId }),
    ...(condition === '' ? {} : { condition }),
    ...(priceRange?.min === undefined || priceRange.min === ''
      ? {}
      : { priceMin: priceRange.min }),
    ...(priceRange?.max === undefined || priceRange.max === ''
      ? {}
      : { priceMax: priceRange.max }),
    ...(stockRange?.min === undefined || stockRange.min === ''
      ? {}
      : { stockMin: stockRange.min }),
    ...(stockRange?.max === undefined || stockRange.max === ''
      ? {}
      : { stockMax: stockRange.max }),
  }

  const query = useQuery({
    queryKey: [
      'products',
      {
        search: debouncedSearch,
        searchMode,
        status,
        missing,
        page,
        applicationModelId,
        priceListId,
        branchId,
        showsPrice,
        showsStock,
        canWrite,
        showsPriceChangedAt,
        // Os filtros de coluna (F152). Sem eles na chave, o cache devolveria a
        // lista sem o filtro e a seta mudaria sozinha.
        //
        // `categoryId` ficou de fora desta lista até a F182, e o efeito era
        // mudo do jeito mais convincente: escolher uma categoria não refazia
        // consulta nenhuma — o React Query respondia com a lista da chave
        // anterior, e a tela mostrava o filtro ligado sobre o resultado de
        // antes dele.
        brand,
        categoryId,
        condition,
        priceMin: priceRange?.min ?? '',
        priceMax: priceRange?.max ?? '',
        stockMin: stockRange?.min ?? '',
        stockMax: stockRange?.max ?? '',
        ...sortKey(sort),
      },
    ],
    queryFn: ({ signal }) =>
      fetchProducts({ ...listRequest, page, pageSize: PAGE_SIZE }, signal),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })

  /*
   * Arquivar pede confirmação, e arquivar ou reativar dizem quando não deu
   * (F132). Antes o botão não perguntava nada e, se o servidor recusasse, a
   * linha simplesmente continuava ali.
   */
  const [archiving, setArchiving] = useState<ProductSummary | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const archive = useMutation({
    mutationFn: async (product: ProductSummary) =>
      await archiveProduct(product.id, product.version),
    onSuccess: async (_result, product) => {
      setRowError(null)
      // O retorno à vista (F248): a linha só sumia, sem dizer qual nem para onde.
      notify.success(
        `${product.name} arquivado. Ele fica em "Arquivados", na Situação.`
      )
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (_error, product) => {
      setRowError(
        `Não foi possível arquivar ${product.name}. Ele pode ter sido alterado por outra pessoa; recarregue a lista e tente de novo.`
      )
    },
    onSettled: () => {
      setArchiving(null)
    },
  })

  const restore = useMutation({
    mutationFn: async (product: ProductSummary) =>
      await restoreProduct(product.id, product.version),
    onSuccess: async (_result, product) => {
      setRowError(null)
      notify.success(`${product.name} reativado.`)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (_error, product) => {
      setRowError(`Não foi possível reativar ${product.name}. Tente de novo.`)
    },
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []

  /*
   * A página que deixou de existir volta para a última que existe (F248).
   * Arquivar os últimos da página 5 de 5 deixava "Página 5 de 4", "Mostrando
   * 101–100" e o vazio dizendo "Nenhum produto cadastrado".
   */
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
  // Ajuste durante a renderização, e não num efeito: o React refaz a tela
  // antes de pintar a página que não existe.
  if (query.isSuccess && page > lastPage) setPage(lastPage)
  // "Aguardando estoque" e "Arquivados" também são filtro: vazio ali não é
  // "nenhum produto cadastrado" (F133).
  const filtered =
    debouncedSearch.trim().length > 0 ||
    applicationModelId !== '' ||
    status !== 'active' ||
    missing !== '' ||
    brand !== '' ||
    categoryId !== '' ||
    condition !== '' ||
    priceRange !== null ||
    stockRange !== null

  /** O filtro corrente, exatamente como a lista o pediu ao servidor. */
  const sessionBranchId = session?.context?.branchId ?? ''
  const currentFilter = useMemo(
    () => ({
      ...(debouncedSearch.trim().length > 0
        ? { search: debouncedSearch.trim(), searchMode }
        : {}),
      status:
        status === 'archived' ? ('archived' as const) : ('active' as const),
      ...(status === 'awaiting' ? { awaitingStock: true } : {}),
      // F247: o mesmo recorte da lista — o lote nunca alcança o que ela não mostra.
      ...(status === 'shelved'
        ? { shelved: 'only' as const }
        : status === 'archived'
          ? {}
          : status === 'sitehidden'
            ? { siteHidden: 'only' as const }
            : { shelved: 'exclude' as const }),
      ...(missing === '' ? {} : { missing: [missing] }),
      /*
       * "Sem estoque" no lote também (F070): a filial é a escolhida ou a da
       * sessão — a mesma que o servidor usa para a lista.
       */
      ...(status === 'out' &&
      stockRange === null &&
      (branchId !== '' || sessionBranchId !== '')
        ? {
            stockBranchId: branchId === '' ? sessionBranchId : branchId,
            stockMax: '0',
          }
        : {}),
      ...(applicationModelId === '' ? {} : { applicationModelId }),
      /*
       * Os filtros de coluna entram AQUI (F152), e é isto que mantém o
       * invariante da F070: o que a lista mostra é exatamente o que a ação em
       * massa muda. Filtrar por marca na tela e mandar o lote sem a marca
       * mudaria o catálogo inteiro anunciando o número certo.
       *
       * As faixas viajam com o contexto delas — a tabela de preço e a filial.
       * Na tabela padrão e na filial da sessão (o que a tela abre), a faixa vai
       * SEM o contexto, e o servidor completa como a lista completa (F248).
       * Antes ela nem ia: "de R$ 0 a R$ 10" na tabela padrão, selecionar tudo
       * e arquivar alcançava o catálogo inteiro.
       */
      ...(brand.trim() === '' ? {} : { brand: brand.trim() }),
      ...(categoryId === '' ? {} : { categoryId }),
      ...(condition === '' ? {} : { condition }),
      ...(priceRange === null
        ? {}
        : {
            ...(priceListId === '' ? {} : { priceListId }),
            ...(priceRange.min === '' ? {} : { priceMin: priceRange.min }),
            ...(priceRange.max === '' ? {} : { priceMax: priceRange.max }),
          }),
      ...(stockRange === null
        ? {}
        : {
            ...(branchId === '' ? {} : { stockBranchId: branchId }),
            ...(stockRange.min === '' ? {} : { stockMin: stockRange.min }),
            ...(stockRange.max === '' ? {} : { stockMax: stockRange.max }),
          }),
    }),
    [
      debouncedSearch,
      searchMode,
      status,
      missing,
      categoryId,
      applicationModelId,
      brand,
      condition,
      priceRange,
      priceListId,
      stockRange,
      branchId,
      sessionBranchId,
    ]
  )

  /*
   * Quais colunas filtram, e como (F152).
   *
   * Declarado aqui e não em `product-columns.tsx` porque o filtro precisa do
   * ESTADO da tela — o valor corrente e quem o muda. A definição da coluna é
   * compartilhada com a exportação, que não tem estado nenhum.
   */
  const columnFilters: Partial<
    Record<
      string,
      {
        filter: ColumnFilterSpec
        value?: ColumnFilterValue
        onFilter: (next: ColumnFilterValue | null) => void
      }
    >
  > = {
    brand: {
      filter: {
        kind: 'enum',
        options: (brands.data ?? []).map((registered) => ({
          value: registered.name,
          label: registered.name,
        })),
      },
      ...(brand === '' ? {} : { value: enumValue(brand) }),
      onFilter: (next) => {
        setBrand(next?.kind === 'enum' ? next.value : '')
        afterColumnFilter()
      },
    },
    category: {
      filter: {
        kind: 'enum',
        options: (categories.data ?? []).map((category) => ({
          value: category.id,
          label: categoryLabel(category),
        })),
      },
      ...(categoryId === '' ? {} : { value: enumValue(categoryId) }),
      onFilter: (next) => {
        setCategoryId(next?.kind === 'enum' ? next.value : '')
        afterColumnFilter()
      },
    },
    condition: {
      filter: {
        kind: 'enum',
        options: (['new', 'used', 'refurbished'] as const).map((value) => ({
          value,
          label: CONDITION_LABEL[value],
        })),
      },
      ...(condition === '' ? {} : { value: enumValue(condition) }),
      onFilter: (next) => {
        setCondition(
          (next?.kind === 'enum' ? next.value : '') as
            '' | ProductSummary['condition']
        )
        afterColumnFilter()
      },
    },
    price: {
      filter: { kind: 'range', unit: 'money' },
      ...(priceRange === null ? {} : { value: priceRange }),
      onFilter: (next) => {
        setPriceRange(next?.kind === 'range' ? next : null)
        afterColumnFilter()
      },
    },
    stock: {
      filter: { kind: 'range', unit: 'quantity' },
      ...(stockRange === null ? {} : { value: stockRange }),
      onFilter: (next) => {
        setStockRange(next?.kind === 'range' ? next : null)
        afterColumnFilter()
      },
    },
  }

  const clearColumnFilters = (): void => {
    setBrand('')
    // A categoria tem chip também (F248): "Limpar tudo" a deixava, e a lista
    // continuava vazia com um chip que ninguém tinha tirado.
    setCategoryId('')
    setCondition('')
    setPriceRange(null)
    setStockRange(null)
    setPage(1)
    resetSelection()
  }

  /**
   * Filtrar por coluna muda o CONJUNTO, então a seleção morre junto (F070).
   *
   * "Marcar todos e tirar alguns" guarda exceções, não escolhidos. Trocar o
   * filtro por baixo de uma seleção de "todos" deixaria as exceções apontando
   * para um conjunto que não é mais aquele — e a ação em massa agiria sobre
   * produtos que ninguém viu.
   */
  const afterColumnFilter = (): void => {
    setPage(1)
    resetSelection()
  }

  const chips: ActiveFilterChip[] = [
    ...(brand === ''
      ? []
      : [
          {
            id: 'brand',
            label: `Marca: ${brand}`,
            onRemove: () => {
              setBrand('')
              afterColumnFilter()
            },
          },
        ]),
    ...(categoryId === ''
      ? []
      : [
          {
            id: 'category',
            label: `Categoria: ${categories.data?.find((category) => category.id === categoryId)?.name ?? '—'}`,
            onRemove: () => {
              setCategoryId('')
              afterColumnFilter()
            },
          },
        ]),
    ...(condition === ''
      ? []
      : [
          {
            id: 'condition',
            label: `Condição: ${CONDITION_LABEL[condition]}`,
            onRemove: () => {
              setCondition('')
              afterColumnFilter()
            },
          },
        ]),
    ...(priceRange === null
      ? []
      : [
          {
            id: 'price',
            label: `Preço: ${rangeLabel(priceRange.min, priceRange.max)}`,
            onRemove: () => {
              setPriceRange(null)
              afterColumnFilter()
            },
          },
        ]),
    ...(stockRange === null
      ? []
      : [
          {
            id: 'stock',
            label: `Saldo: ${quantityRangeLabel(
              stockRange.min,
              stockRange.max
            )}`,
            onRemove: () => {
              setStockRange(null)
              afterColumnFilter()
            },
          },
        ]),
  ]

  const isSelected = (id: string): boolean =>
    selection.all ? !selection.excluded.has(id) : selection.included.has(id)

  /** Marca ou desmarca um — pela caixinha ou, com algo marcado, pela linha. */
  const toggleOne = (id: string): void => {
    setSelection((current) =>
      current.all
        ? { all: true, excluded: withToggled(current.excluded, id) }
        : { all: false, included: withToggled(current.included, id) }
    )
    setBulkResult(null)
  }

  const selectedCount = selection.all
    ? Math.max(0, total - selection.excluded.size)
    : selection.included.size

  /** Nenhum, todos, ou o terceiro estado que os dois anteriores não dizem. */
  const headerState: boolean | 'indeterminate' =
    selectedCount === 0
      ? false
      : selection.all && selection.excluded.size === 0
        ? true
        : 'indeterminate'

  const stockTracking = useMutation({
    mutationFn: async (tracksStock: boolean) =>
      await setProductsStockTracking({
        tracksStock,
        filter: currentFilter,
        ...(selection.all
          ? { excludeIds: [...selection.excluded] }
          : { includeIds: [...selection.included] }),
      }),
    onSuccess: async ({ changed }) => {
      /*
       * Diz o que aconteceu, com o número — e diz também quando não aconteceu
       * nada. "Pronto" depois de um lote em que todos os produtos já estavam
       * como se pediu anunciaria um trabalho que não houve.
       */
      setBulkResult(
        changed === 0
          ? 'Nenhum produto foi alterado: todos já estavam assim.'
          : changed === 1
            ? '1 produto alterado.'
            : `${String(changed)} produtos alterados.`
      )
      setSelection(NOTHING_SELECTED)
      setConfirming(null)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })

  /*
   * F247 — arquivar até ter estoque, e mostrar de novo, pelo mesmo conjunto
   * da F070. O servidor confere o saldo de TODAS as filiais e diz quantos
   * poupou (`kept`): a tela conta isso, em vez de deixar achar que arquivou
   * tudo.
   */
  const shelf = useMutation({
    mutationFn: async (action: 'shelve' | 'unshelve') => {
      const input = {
        filter: currentFilter,
        ...(selection.all
          ? { excludeIds: [...selection.excluded] }
          : { includeIds: [...selection.included] }),
      }
      return action === 'shelve'
        ? { action, ...(await shelveProducts(input)) }
        : { action, ...(await unshelveProducts(input)) }
    },
    onSuccess: async ({ action, changed, kept }) => {
      setBulkResult(shelfMessage(action, changed, kept))
      setSelection(NOTHING_SELECTED)
      setShelfConfirm(null)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: () => {
      setShelfConfirm(null)
      setBulkResult('Não foi possível concluir. Tente de novo.')
    },
  })

  /* F291 — ocultar da loja online, com estoque ou sem, e mostrar de novo. */
  const site = useMutation({
    mutationFn: async (action: 'hide' | 'show') => {
      const input = {
        filter: currentFilter,
        ...(selection.all
          ? { excludeIds: [...selection.excluded] }
          : { includeIds: [...selection.included] }),
      }
      return {
        action,
        ...(action === 'hide'
          ? await hideProductsFromStorefront(input)
          : await showProductsOnStorefront(input)),
      }
    },
    onSuccess: async ({ action, changed }) => {
      const n =
        changed === 1 ? '1 produto' : `${String(changed)} produtos`
      setBulkResult(
        changed === 0
          ? 'Nenhum produto mudou.'
          : action === 'hide'
            ? `${n} fora do site.`
            : `${n} de volta no site.`
      )
      setSelection(NOTHING_SELECTED)
      setSiteConfirm(null)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: () => {
      setSiteConfirm(null)
      setBulkResult('Não foi possível concluir. Tente de novo.')
    },
  })

  const openNew = (): void => {
    setEditing(undefined)
    setFormOpen(true)
  }

  const openEdit = (product: ProductSummary): void => {
    if (priceListId === '') {
      setEditing(product)
      setFormOpen(true)
      return
    }
    /*
     * O "Preço de venda" do Editar é o da tabela PADRÃO — é nela que o
     * formulário grava (F248). Com a lista mostrando outra tabela, a linha traz
     * o preço daquela, e o formulário abria com ele: quem corrigia o preço de
     * atacado gravava, sem saber, o preço de varejo. Aqui o formulário abre com
     * o preço da padrão, buscado na hora.
     */
    const standard = priceLists.data?.priceLists.find((list) => list.isDefault)
    const { price: _otherListPrice, ...withoutPrice } = product
    void fetchProductLabelInfo([product.id])
      .then((info) => {
        const salePrice = info.items[0]?.salePrice ?? null
        setEditing(
          standard === undefined
            ? withoutPrice
            : {
                ...withoutPrice,
                price:
                  salePrice === null
                    ? null
                    : {
                        priceListId: standard.id,
                        priceListName: standard.name,
                        basePrice: salePrice,
                      },
              }
        )
      })
      .catch(() => {
        // Sem o preço da padrão, o campo abre vazio — nunca com o da outra.
        setEditing(withoutPrice)
      })
      .finally(() => {
        setFormOpen(true)
      })
  }

  /** A ficha do produto (F151): clicar na linha abre aqui, não na edição. */
  const [viewing, setViewing] = useState<ProductSummary | null>(null)

  return (
    <Page>
      <PageHeader
        title="Produtos"
        description="Cadastro do catálogo. Preço e estoque têm módulos próprios."
        actions={
          canWrite ? (
            <div className="flex items-center gap-2">
              {/* F163: marcar o catálogo antigo de uma vez, com prévia. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCategorizing(true)
                }}
              >
                Classificar pelo nome
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSettingMinimum(true)
                }}
              >
                Estoque mínimo por categoria
              </Button>
              {/* F243: a árvore e as categorias de outra loja, por planilha. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setImportingCategories(true)
                }}
              >
                Importar planilha de categorias
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={openNew}
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              >
                Novo produto
              </Button>
            </div>
          ) : undefined
        }
      />

      <ListToolbar
        actions={
          <>
            <ColumnChooser
              columns={offeredColumns}
              visible={shownColumns.map((column) => column.id)}
              onToggle={(id) => {
                columnsPreference.save({
                  visible: toggledColumns(columnsPreference.value.visible, id),
                })
              }}
              onReset={() => {
                columnsPreference.save({
                  visible: [...DEFAULT_PRODUCT_COLUMNS],
                })
              }}
            />
            <ExportMenu
              fileName="produtos"
              sheetName="Produtos"
              columns={productExportColumns(shownColumns)}
              fetchPage={(exportPage) =>
                fetchProducts({
                  ...listRequest,
                  page: exportPage,
                  pageSize: 100,
                })
              }
              pageSize={100}
              total={query.data?.total ?? 0}
            />
          </>
        }
      >
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            // Reset here rather than in an effect watching the debounced
            // value: a new search term always means page one, and doing it
            // in the handler avoids a render cascade.
            setPage(1)
            // Outra busca é outro conjunto. Ver `resetSelection`.
            resetSelection()
          }}
          placeholder="Buscar por nome, código ou código de barras"
          aria-label="Buscar produtos"
        />

        <SearchModeToggle
          mode={searchMode}
          onChange={(next) => {
            searchModePreference.save({
              ...searchModePreference.value,
              products: next,
            })
            setPage(1)
            // Outro conjunto: a seleção de antes não fala deste. Ver
            // `resetSelection`.
            resetSelection()
          }}
        />

        <Select
          value={status}
          onChange={(event) => {
            setStatus(
              event.target.value as
                'active' | 'archived' | 'awaiting' | 'out' | 'shelved' | 'sitehidden'
            )
            setPage(1)
            resetSelection()
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="active">Ativos</option>
          <option value="awaiting">Aguardando estoque</option>
          {showsStock ? <option value="out">Sem estoque</option> : null}
          <option value="shelved">Arquivados até ter estoque</option>
          <option value="sitehidden">Ocultos do site</option>
          <option value="archived">Arquivados</option>
        </Select>

        {/* F247: o que falta no cadastro — foto, categoria ou preço. */}
        <Select
          value={missing}
          onChange={(event) => {
            setMissing(event.target.value as '' | MissingField)
            setPage(1)
            resetSelection()
          }}
          aria-label="Falta no cadastro"
          className="w-44"
        >
          <option value="">Todos</option>
          <option value="photo">Sem foto</option>
          <option value="category">Sem categoria</option>
          <option value="price">Sem preço</option>
        </Select>

        {/*
          Categoria e Marca na BARRA, e não só no menu da coluna (F182).

          O dono circulou os selects do Estoque e pediu os mesmos aqui. A razão
          não é simetria: no menu do cabeçalho (F152), desligar a coluna em
          "Colunas" **some com o filtro** — a tela perde uma capacidade porque
          alguém escondeu uma coluna. Na barra, o filtro existe independente do
          que está à mostra.

          O estado é o MESMO dos filtros de coluna, de propósito: o chip do
          `ActiveFilters` continua valendo para os dois caminhos, e a ação em
          massa da F070 continua enxergando exatamente o filtro que a lista
          está mostrando.
        */}
        {(categories.data ?? []).length === 0 ? null : (
          <Select
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value)
              setPage(1)
              resetSelection()
            }}
            aria-label="Categoria"
            className="w-56"
          >
            <option value="">Todas as categorias</option>
            {(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {categoryLabel(category)}
              </option>
            ))}
          </Select>
        )}

        {(brands.data ?? []).length === 0 ? null : (
          <Select
            value={brand}
            onChange={(event) => {
              setBrand(event.target.value)
              setPage(1)
              resetSelection()
            }}
            aria-label="Marca"
            className="w-56"
          >
            <option value="">Todas as marcas</option>
            {(brands.data ?? []).map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </Select>
        )}

        {/* Only once the shop has a register. A filter over an empty vocabulary
            is a control that can only ever return nothing. */}
        {(models.data?.models.length ?? 0) > 0 || applicationModelId !== '' ? (
          <div className="w-64">
            <Combobox
              value={applicationModelId === '' ? null : applicationModelId}
              onValueChange={(value) => {
                setApplicationModelId(value ?? '')
                setPage(1)
                resetSelection()
              }}
              search={modelSearch}
              onSearchChange={setModelSearch}
              loading={models.isFetching}
              placeholder="Serve em..."
              aria-label="Filtrar por aplicação"
              items={(models.data?.models ?? []).map((model) => ({
                value: model.id,
                label: applicationLabel(model),
              }))}
            />
          </div>
        ) : null}

        {/* The two columns on the right are answers about a particular table
            and a particular shop, so the screen says which — and lets somebody
            change it. */}
        {showsPrice && (priceLists.data?.priceLists.length ?? 0) > 1 ? (
          <Select
            value={priceListId}
            onChange={(event) => {
              setPriceListId(event.target.value)
              setPage(1)
            }}
            aria-label="Tabela de preço"
            className="w-48"
          >
            <option value="">Tabela padrão</option>
            {(priceLists.data?.priceLists ?? []).map((list) => (
              <option key={list.id} value={list.id}>
                {list.name}
              </option>
            ))}
          </Select>
        ) : null}

        {showsStock && (branches.data?.branches.length ?? 0) > 1 ? (
          <Select
            value={branchId}
            onChange={(event) => {
              setBranchId(event.target.value)
              setPage(1)
            }}
            aria-label="Filial do estoque"
            className="w-48"
          >
            <option value="">Filial atual</option>
            {(branches.data?.branches ?? []).map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        ) : null}
      </ListToolbar>

      <ActiveFilters chips={chips} onClearAll={clearColumnFilters} />

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Nenhum produto encontrado"
      />

      {rowError === null ? null : (
        <InlineAlert tone="danger">{rowError}</InlineAlert>
      )}

      {/*
        A barra de ação, presente só quando há seleção (F070).

        O `aria-live` fica no invólucro, que está SEMPRE no DOM, e não na barra
        que aparece e some. Uma região viva criada no mesmo instante em que
        ganha conteúdo costuma não anunciar nada: o leitor de tela precisa dela
        montada antes para reparar que mudou. Escrevi errado na primeira versão,
        e o sintoma seria justamente o silêncio.

        A contagem é o que se anuncia porque é a informação: quem tira exceções
        pelo teclado precisa ouvir 5309 virar 5308 sem ver a caixa mudar de cor.
        E ela é texto, nunca só o estado visual das caixas — em quatro páginas
        de tabela, o número é a única coisa que diz o tamanho do que está
        prestes a acontecer.
      */}
      {labelProduct === null ? null : (
        <LabelPrintDialog
          product={labelProduct}
          onClose={() => {
            setLabelProduct(null)
          }}
        />
      )}

      <div aria-live="polite">
        {canWrite && selectedCount > 0 ? (
          <div className="flex flex-wrap items-center gap-3 rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) px-3 py-2">
            <span className="text-(length:--text-sm) text-(--text-primary)">
              {selectedCount === 1
                ? '1 produto selecionado'
                : `${count(selectedCount)} produtos selecionados`}
            </span>

            {/*
              Desabilitadas quando a LOJA não conta (F102).

              Vivas, seriam dois botões que escrevem no cadastro e não mudam
              nada no balcão — a objeção da F070 pelo outro lado. O `title`
              carrega a mesma razão que a caixa do produto mostra.
            */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={stockOff !== null}
                title={stockOff ?? undefined}
                onClick={() => {
                  setConfirming(false)
                }}
              >
                Deixar de controlar estoque
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={stockOff !== null}
                title={stockOff ?? undefined}
                onClick={() => {
                  setConfirming(true)
                }}
              >
                Voltar a controlar
              </Button>
              {/*
                F247: o produto sem estoque sai da vista e volta sozinho com a
                primeira entrada. Na vista dos arquivados até ter estoque, o
                caminho de volta à mão.
              */}
              {status === 'shelved' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShelfConfirm('unshelve')
                  }}
                >
                  Mostrar de novo
                </Button>
              ) : status === 'archived' ? null : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShelfConfirm('shelve')
                  }}
                >
                  Arquivar sem estoque
                </Button>
              )}
              {/* F291: tira da loja online na hora, com estoque ou sem. */}
              {status === 'sitehidden' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSiteConfirm('show')
                  }}
                >
                  Mostrar no site
                </Button>
              ) : status === 'archived' ? null : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSiteConfirm('hide')
                  }}
                >
                  Ocultar do site
                </Button>
              )}
              {/*
                Etiquetas em lote (F124).

                Só com os produtos MARCADOS, e até cem: "selecionar tudo" é um
                filtro que atravessa páginas, e a etiqueta precisa dos produtos
                de verdade. Desabilitado com o motivo em vez de etiquetar só
                parte da seleção sem dizer.
              */}
              <Button
                variant="secondary"
                size="sm"
                loading={labelBatch.pending}
                disabled={selection.all || selectedCount > 100}
                title={
                  selection.all
                    ? 'Marque os produtos um a um para etiquetar em lote.'
                    : selectedCount > 100
                      ? 'Etiquete até 100 produtos por vez.'
                      : undefined
                }
                leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  const tenantId = session?.context?.tenantId ?? null
                  if (tenantId === null || selection.all) return
                  setLabelBatch({ pending: true, error: null })
                  fetchProductLabelInfo([...selection.included])
                    .then(async (info) => {
                      const { dropped } = addToLabelQueue(
                        tenantId,
                        info.items.map((item) => ({
                          variantId: item.variantId,
                          productId: item.productId,
                          name: item.name,
                          sku: item.sku,
                          gtin: item.gtin,
                          salePrice: item.salePrice,
                          brand: item.brand,
                          unit: item.unit,
                          quantity: 1,
                        }))
                      )
                      setSelection(NOTHING_SELECTED)
                      // O que não coube é dito aqui (F195): ir para
                      // Etiquetas esconderia o aviso.
                      const full = droppedMessage(dropped)
                      setLabelBatch({ pending: false, error: full })
                      if (full === null) await navigate({ to: '/etiquetas' })
                    })
                    .catch(() => {
                      setLabelBatch({
                        pending: false,
                        error:
                          'Não foi possível buscar os produtos para as etiquetas.',
                      })
                    })
                }}
              >
                Etiquetas
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelection(NOTHING_SELECTED)
                }}
              >
                Limpar seleção
              </Button>
              {labelBatch.error === null ? null : (
                <InlineAlert tone="danger" className="w-full">
                  {labelBatch.error}
                </InlineAlert>
              )}
            </div>
          </div>
        ) : null}

        {/* Mesmo invólucro vivo, pela mesma razão. */}
        {bulkResult === null ? null : (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {bulkResult}
          </p>
        )}
      </div>

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="os produtos"
        isEmpty={items.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setStatus('active')
          setMissing('')
          setSearch('')
          setApplicationModelId('')
          clearColumnFilters()
          resetSelection()
        }}
        empty={
          <EmptyState
            icon={<Package aria-hidden="true" className="h-8 w-8" />}
            title={
              status === 'archived'
                ? 'Nenhum produto arquivado'
                : 'Nenhum produto cadastrado'
            }
            description={
              canWrite
                ? 'Cadastre o primeiro produto para começar.'
                : 'Você não tem permissão para cadastrar produtos.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {canWrite ? (
                  <TableHeaderCell className="w-10">
                    <Checkbox
                      checked={headerState}
                      onCheckedChange={() => {
                        /*
                         * Um clique só, com dois destinos: havendo qualquer
                         * coisa marcada, limpa; não havendo, marca tudo o que
                         * casa o filtro. É o que a caixa de cabeçalho faz em
                         * toda tabela que a pessoa já usou, e o terceiro
                         * estado nunca é um destino — só um retrato.
                         */
                        setSelection(
                          selectedCount > 0
                            ? NOTHING_SELECTED
                            : { all: true, excluded: new Set() }
                        )
                        setBulkResult(null)
                      }}
                      aria-label={
                        selectedCount > 0
                          ? 'Limpar seleção'
                          : `Selecionar todos os ${count(total)} produtos do filtro`
                      }
                    />
                  </TableHeaderCell>
                ) : null}
                {shownColumns.map((column) => {
                  const filter = columnFilters[column.id]

                  // Sem ordenação e sem filtro: continua um cabeçalho comum, e
                  // não um menu que abre para não oferecer nada.
                  if (column.sortKey === undefined && filter === undefined) {
                    return (
                      <TableHeaderCell
                        key={column.id}
                        className={column.headerClassName ?? ''}
                      >
                        {column.header ?? column.label}
                      </TableHeaderCell>
                    )
                  }

                  return (
                    <FilterableHeaderCell
                      key={column.id}
                      className={column.headerClassName ?? ''}
                      {...(column.sortKey === undefined
                        ? {}
                        : sort.column(column.sortKey))}
                      {...(column.numeric === true
                        ? { kind: 'number' as const, numeric: true }
                        : {})}
                      {...(filter ?? {})}
                    >
                      {column.label}
                    </FilterableHeaderCell>
                  )
                })}
                {/* Dropped entirely when there is nothing to put under it,
                    rather than left empty. A column headed "Ações" with
                    nothing under it reads as a broken screen, not as a
                    permission boundary. Imprimir etiqueta (F124) não é
                    escrita: quem enxerga o produto pode colar o código de
                    barras dele numa peça, então a coluna existe para ele. */}
                {canWrite || viewingDefaultPriceList ? (
                  // Right-aligned, but not `numeric`: that variant also
                  // switches to a monospace font, which is right for money and
                  // wrong for buttons.
                  <TableActionsHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableActionsHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((product) => (
                <TableRow
                  key={product.id}
                  selected={isSelected(product.id)}
                  onOpen={() => {
                    setViewing(product)
                  }}
                  /*
                   * Com algo marcado, a linha marca em vez de abrir (F248): a
                   * pessoa está escolhendo produtos para a ação em massa. A
                   * ficha continua no nome.
                   */
                  selecting={canWrite && selectedCount > 0}
                  onToggle={() => {
                    toggleOne(product.id)
                  }}
                >
                  {canWrite ? (
                    /*
                     * A caixa marca, e só marca — e a célula INTEIRA é dela
                     * (F248). Era uma caixinha de 16 px numa célula morta de
                     * 40: errar por pouco não marcava nada, ou caía na foto e
                     * abria a ficha. O `label` estende o alvo à célula toda.
                     */
                    <TableCell className="p-0">
                      <label className="flex h-(--row-height) min-w-10 cursor-pointer items-center justify-center px-3">
                        <Checkbox
                          checked={isSelected(product.id)}
                          onCheckedChange={() => {
                            toggleOne(product.id)
                          }}
                          // O nome do produto, e não "selecionar item 7": quem
                          // navega por leitor de tela ouve a linha, não a vê.
                          aria-label={`Selecionar ${product.name}`}
                        />
                      </label>
                    </TableCell>
                  ) : null}
                  {shownColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      numeric={column.numeric === true}
                      className={column.className ?? ''}
                    >
                      {column.id === 'name' ? (
                        <RowOpener
                          onOpen={() => {
                            setViewing(product)
                          }}
                        >
                          {column.cell(product)}
                        </RowOpener>
                      ) : (
                        column.cell(product)
                      )}
                    </TableCell>
                  ))}
                  {canWrite || viewingDefaultPriceList ? (
                    <TableActionsCell>
                      {/*
                        Editar à vista, e o resto no ⋯ (F132). Quem não edita
                        vê a Etiqueta à vista — é a única ação que tem.
                      */}
                      <RowActions
                        name={product.name}
                        primary={
                          canWrite ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                openEdit(product)
                              }}
                              leadingIcon={
                                <Pencil
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                            >
                              Editar
                            </Button>
                          ) : viewingDefaultPriceList &&
                            product.status === 'active' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                openLabel(product)
                              }}
                              leadingIcon={
                                <Printer
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                            >
                              Etiqueta
                            </Button>
                          ) : undefined
                        }
                        items={[
                          canWrite &&
                            viewingDefaultPriceList &&
                            product.status === 'active' && {
                              label: 'Imprimir etiqueta',
                              icon: (
                                <Printer
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ),
                              onSelect: () => {
                                openLabel(product)
                              },
                            },
                          canWrite &&
                            product.status !== 'active' && {
                              label: 'Reativar',
                              icon: (
                                <RotateCcw
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ),
                              disabled: restore.isPending,
                              onSelect: () => {
                                restore.mutate(product)
                              },
                            },
                          canWrite &&
                            product.status === 'active' && {
                              label: 'Arquivar',
                              danger: true,
                              icon: (
                                <Archive
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ),
                              onSelect: () => {
                                setArchiving(product)
                              },
                            },
                        ]}
                      />
                    </TableActionsCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {categorizing ? (
        <CategorizeProductsDialog
          onClose={() => {
            setCategorizing(false)
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['products'] })
          }}
        />
      ) : null}

      {settingMinimum ? (
        <MinimumStockDialog
          onClose={() => {
            setSettingMinimum(false)
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['products'] })
          }}
        />
      ) : null}

      {importingCategories ? (
        <ImportCategoriesDialog
          onClose={() => {
            setImportingCategories(false)
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['products'] })
          }}
        />
      ) : null}

      {viewing === null ? null : (
        <ProductDetailDialog
          product={viewing}
          onClose={() => {
            setViewing(null)
          }}
          {...(canWrite
            ? {
                onEdit: () => {
                  openEdit(viewing)
                  setViewing(null)
                },
              }
            : {})}
        />
      )}

      {/* Mounted only while open, and keyed by the product being edited, so
          the form starts from the right values without an effect that resets
          them. React's own answer to "reset state when a prop changes". */}
      {formOpen ? (
        <ProductForm
          key={editing?.id ?? 'new'}
          product={editing}
          defaultTracksStock={
            company.data?.company.salesFlow.tracksStockDefault
          }
          stockDisabledBecause={stockOff}
          onClose={() => {
            setFormOpen(false)
          }}
        />
      ) : null}

      {/*
        A frase carrega a contagem E o que a mudança não faz.

        `docs/UI_UX.md` proíbe "Tem certeza?" e exige o dado na frase — aqui o
        dado é quantos produtos. A segunda metade é igualmente necessária, e
        por um motivo diferente: mexer em 5309 produtos de uma vez só é uma
        decisão confortável para quem sabe que ela volta atrás. Nenhum saldo e
        nenhum movimento são apagados; religar devolve o número que estava lá.
        Sem dizer isso, a resposta racional a este botão é não clicar.
      */}
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(next) => {
          if (!next) setArchiving(null)
        }}
        title="Arquivar produto"
        description={
          archiving === null
            ? ''
            : `Arquivar ${archiving.name}? Ele some das buscas do PDV e das listas do dia a dia. Vendas, estoque e histórico continuam, e dá para reativar em Produtos → Arquivados.`
        }
        confirmLabel="Arquivar"
        loading={archive.isPending}
        onConfirm={() => {
          if (archiving !== null) archive.mutate(archiving)
        }}
      />

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null)
        }}
        tone="neutral"
        title={
          confirming === true
            ? 'Voltar a controlar estoque'
            : 'Deixar de controlar estoque'
        }
        description={
          confirming === true
            ? `${count(selectedCount)} ${selectedCount === 1 ? 'produto volta' : 'produtos voltam'} a ter saldo, e a venda passa a ser recusada por falta. O saldo que existia antes reaparece: nada foi apagado.`
            : `${count(selectedCount)} ${selectedCount === 1 ? 'produto deixa' : 'produtos deixam'} de ter saldo. Vender não movimenta nada e nunca é recusado por falta. Nenhum saldo ou movimento é apagado — dá para voltar atrás a qualquer momento.`
        }
        confirmLabel={
          confirming === true ? 'Voltar a controlar' : 'Deixar de controlar'
        }
        loading={stockTracking.isPending}
        onConfirm={() => {
          stockTracking.mutate(confirming === true)
        }}
      />

      <ConfirmDialog
        open={shelfConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setShelfConfirm(null)
        }}
        tone="neutral"
        title={
          shelfConfirm === 'unshelve'
            ? 'Mostrar de novo'
            : 'Arquivar até ter estoque'
        }
        description={
          shelfConfirm === 'unshelve'
            ? `${count(selectedCount)} ${selectedCount === 1 ? 'produto volta' : 'produtos voltam'} a aparecer na lista, na loja online, no robô e no balcão.`
            : `${count(selectedCount)} ${selectedCount === 1 ? 'produto selecionado some' : 'produtos selecionados somem'} da lista, da loja online, do robô e do balcão — só os sem estoque em nenhuma filial. Continuam no Estoque, nas Compras e na nota de compra, e voltam sozinhos quando entrar estoque.`
        }
        // Um nome só para a marca (F248): o botão dizia "Arquivar", a mesma
        // palavra do arquivar de sempre, que é outra coisa.
        confirmLabel={
          shelfConfirm === 'unshelve'
            ? 'Mostrar de novo'
            : 'Arquivar até ter estoque'
        }
        loading={shelf.isPending}
        onConfirm={() => {
          if (shelfConfirm !== null) shelf.mutate(shelfConfirm)
        }}
      />
      <ConfirmDialog
        open={siteConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setSiteConfirm(null)
        }}
        tone="neutral"
        title={siteConfirm === 'show' ? 'Mostrar no site' : 'Ocultar do site'}
        description={
          siteConfirm === 'show'
            ? `${count(selectedCount)} ${selectedCount === 1 ? 'produto volta' : 'produtos voltam'} a aparecer na loja online.`
            : `${count(selectedCount)} ${selectedCount === 1 ? 'produto sai' : 'produtos saem'} da loja online (GV e Ipatinga), com estoque ou sem. No balcão, no Estoque e no resto do Aeris continua igual.`
        }
        confirmLabel={
          siteConfirm === 'show' ? 'Mostrar no site' : 'Ocultar do site'
        }
        loading={site.isPending}
        onConfirm={() => {
          if (siteConfirm !== null) site.mutate(siteConfirm)
        }}
      />
    </Page>
  )
}
